import {
  HistoryOutlined,
  InboxOutlined,
  LogoutOutlined,
  SwapOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Avatar, Dropdown, Layout, Space, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router-dom';

import {
  DEV_IDENTITIES,
  type DevIdentity,
  getDevIdentity,
  isDevLoginHeaderEnabled,
  setDevIdentity,
} from '@/auth/devLogin';
import { defaultRouteForRole } from '@/auth/RequireRole';
import { useRoles } from '@/auth/useRoles';
import { invalidateSession } from '@/auth/useSession';

import { AppFooter } from './AppFooter';
import { LanguageSwitcher } from './LanguageSwitcher';

const { Header, Content } = Layout;

const HEADER_BG = '#0179C1';
const HEADER_HEIGHT = 85;
const LOGO_SRC = '/bmo-global-asset-management.svg';

export function AppShell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role, name } = useRoles();
  const showDevLoginHeader = isDevLoginHeaderEnabled();
  const devIdentity = showDevLoginHeader ? getDevIdentity() : null;

  const goHome = () => navigate(role ? defaultRouteForRole(role) : '/');

  const switchIdentity = (next: DevIdentity) => {
    setDevIdentity(next.id);
    // Reset the remembered active-tab for BOTH dashboards so a
    // Dev-Login switch always lands on the natural first tab
    // (Draft for Originator, Published for Approver) rather than
    // whatever tab the previous identity happened to leave open.
    // localStorage is shared across identities on the same browser,
    // so without this a returning identity re-inherits the outgoing
    // one's last view (most annoyingly, the Audit History tab).
    window.localStorage.removeItem('bmo-inflow.originator.activeTab');
    window.localStorage.removeItem('bmo-inflow.approver.activeTab');
    // The /me response is cached module-level for the life of the document,
    // so a client-side navigate leaves every session-driven feature answering
    // as the OUTGOING identity while the header — which reads sessionStorage
    // directly — already shows the new one. That split is how "switched to
    // Frank, form still prefilled Eve's phone and rep code" happened.
    //
    // Drop the cache AND do a full document load: identity is an
    // application-wide axis, and any module holding derived state (roles,
    // session timer, in-flight form values) would otherwise survive the
    // switch. A reload is what signing out and back in does anyway, and this
    // is a Dev-only affordance where being obviously correct beats being
    // smooth.
    invalidateSession();
    window.location.assign(defaultRouteForRole(next.role));
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Bypass Blocks (WCAG 2.1 — 2.4.1, Level A). The header and, inside the
          form runtime, a 15-step rail repeat on every page; without this a
          keyboard user tabs through all of it before reaching the content, on
          every single step. Visually hidden until focused. */}
      <a href="#main-content" className="skip-link">
        {t('a11y.skipToContent')}
      </a>
      <Header
        style={{
          height: HEADER_HEIGHT,
          lineHeight: 'normal',
          padding: '0 32px',
          background: HEADER_BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Reverse logo (red roundel + white wordmark) — sits directly on the
            blue header, no backplate, no filter. */}
        {/* The logo navigates home, so it is a control and must be one: an
            <img onClick> is not focusable and is never announced as actionable
            (WCAG 2.1.1, 4.1.2). Wrapping in a real <button> gives it a tab
            stop, Enter/Space, and a name — and the alt text moves onto the
            button so it isn't announced twice. */}
        <button
          type="button"
          onClick={goHome}
          aria-label={t('a11y.goHome')}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            display: 'block',
          }}
        >
          <img src={LOGO_SRC} alt="" style={{ height: 28, display: 'block' }} />
        </button>

        <Space size="middle" align="center">
          {showDevLoginHeader && devIdentity && (
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              menu={{
                selectable: true,
                selectedKeys: [devIdentity.id],
                items: DEV_IDENTITIES.map((i) => ({
                  key: i.id,
                  label: (
                    <Space>
                      <span>{i.name}</span>
                      <Tag color={i.role === 'Originator' ? 'blue' : 'green'}>{i.role}</Tag>
                    </Space>
                  ),
                  onClick: () => switchIdentity(i),
                })),
              }}
            >
              <button
                type="button"
                aria-label="switch-identity"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  border: 'none',
                  padding: '4px 10px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                <SwapOutlined /> Switch user
              </button>
            </Dropdown>
          )}

          <LanguageSwitcher />

          {role && (
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              menu={{
                items: [
                  // Audit History — opens the Recent Decisions tab on the
                  // caller's own dashboard. The BE endpoint is scoped so
                  // Originators only see the workflows they own; Approvers
                  // see the full firm-wide feed. Same landing behaviour for
                  // both roles: the tab-remembering hook picks up the
                  // localStorage key we prime here on the very next render.
                  ...(role === 'Approver' || role === 'Originator'
                    ? [
                        {
                          key: 'audit',
                          icon: <HistoryOutlined />,
                          label: t('auth.auditHistory', {
                            defaultValue: 'Audit history',
                          }),
                          // Dedicated page (not a dashboard tab) — same
                          // route pattern used elsewhere in the app:
                          // /<role>/audit-history. The page itself
                          // scopes the feed via the BE endpoint, which
                          // filters by owner OID for originators.
                          onClick: () =>
                            navigate(
                              role === 'Approver'
                                ? '/approver/audit-history'
                                : '/originator/audit-history',
                            ),
                        },
                        {
                          // Archive — same route pattern and the same menu
                          // slot as Audit history. Both roles can VIEW it (per
                          // the ticket); only the Approver sees a Restore
                          // action once inside.
                          key: 'archive',
                          icon: <InboxOutlined />,
                          label: t('workflow.archive.title', {
                            defaultValue: 'Archived Workflows',
                          }),
                          onClick: () =>
                            navigate(
                              role === 'Approver' ? '/approver/archive' : '/originator/archive',
                            ),
                        },
                        { key: 'audit-sep', type: 'divider' as const },
                      ]
                    : []),
                  {
                    key: 'signout',
                    icon: <LogoutOutlined />,
                    label: t('auth.signOut'),
                    onClick: () => navigate('/auth/logout'),
                  },
                ],
              }}
            >
              <div className="app-user-trigger" aria-label="user-menu">
                <span
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    lineHeight: 1.25,
                  }}
                >
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>
                    {name ?? '—'}
                  </span>
                  <span style={{ color: 'rgba(255,255,255,0.9)', fontWeight: 400, fontSize: 14 }}>
                    {role}
                  </span>
                </span>
                <Avatar
                  size={40}
                  shape="square"
                  icon={<UserOutlined />}
                  style={{
                    background: 'rgba(255,255,255,0.18)',
                    color: '#fff',
                    borderRadius: 8,
                  }}
                />
              </div>
            </Dropdown>
          )}
        </Space>
      </Header>

      <Content
        style={{
          padding: '24px 32px',
          background: '#f5f6f7',
          // Fill the viewport minus header so the footer is pinned to the
          // bottom even on short pages, and short pages still feel "full".
          minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* `<main>` is the landmark the skip link targets and the one screen
            readers offer as "jump to main content". `tabIndex={-1}` so the
            anchor can actually move focus here — without it the browser scrolls
            but leaves focus behind, and the next Tab returns to the header. */}
        <main
          id="main-content"
          tabIndex={-1}
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            outline: 'none',
          }}
        >
          <Outlet />
        </main>
      </Content>
      <AppFooter />
    </Layout>
  );
}
